import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserManagementService } from '../../../services/user-management.service';
import { User } from '../../../interfaces/product.interface';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  users: User[] = [];
  filteredUsers: User[] = [];
  paginatedUsers: User[] = [];
  loading = false;
  currentPage = 1;
  pageSize = 25;
  totalPages = 1;
  startIndex = 0;
  endIndex = 0;
  searchTerm = '';
  selectedRole = 'all';
  sortField = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';
  
  // Modal states
  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showRoleModal = false;
  showStatusModal = false;
  
  // Selected user for operations
  selectedUser: User | null = null;
  
  // Forms
  createUserForm: FormGroup;
  editUserForm: FormGroup;
  
  // Statistics
  statistics = {
    totalUsers: 0,
    totalAdmins: 0,
    totalSuperAdmins: 0,
    activeUsers: 0
  };
  
  // Current user for permission checks
  currentUser: User | null = null;
  
  private subscriptions: Subscription[] = [];

  constructor(
    private userManagementService: UserManagementService,
    private authService: AuthService,
    private fb: FormBuilder,
    private toastService: ToastService
  ) {
    this.createUserForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      gender: ['male', Validators.required],
      role: ['user', Validators.required],
      dateOfBirth: ['', Validators.required],
      fullAddress: ['', Validators.required],
      altMobile: ['']
    });

    this.editUserForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      gender: ['male', Validators.required],
      role: ['user', Validators.required],
      dateOfBirth: ['', Validators.required],
      fullAddress: ['', Validators.required],
      altMobile: ['']
    });
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser();
  }

  loadUsers(): void {
    this.loading = true;
    this.currentPage = 1;
    
    const sub = this.userManagementService.getUsers().subscribe({
      next: (response) => {
        this.users = response.users;
        this.filteredUsers = response.users;
        this.updateStatistics();
        this.updatePagination();
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading users:', error);
        this.loading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  // Pagination methods
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 3; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  private updatePagination(): void {
    this.startIndex = (this.currentPage - 1) * this.pageSize;
    this.endIndex = Math.min(this.startIndex + this.pageSize, this.filteredUsers.length);
    this.totalPages = Math.ceil(this.filteredUsers.length / this.pageSize);
    
    // Apply sorting and pagination
    this.applySortingAndPagination();
  }

  private applySortingAndPagination(): void {
    // First apply sorting
    let sorted = [...this.filteredUsers];
    
    sorted.sort((a, b) => {
      let aValue: any = a[this.sortField as keyof User];
      let bValue: any = b[this.sortField as keyof User];
      
      // Handle special cases
      if (this.sortField === 'registrationDate') {
        // Handle Firestore Timestamp
        if (aValue && typeof aValue === 'object' && aValue.toDate) {
          aValue = aValue.toDate();
        } else {
          aValue = new Date(aValue || 0);
        }
        
        if (bValue && typeof bValue === 'object' && bValue.toDate) {
          bValue = bValue.toDate();
        } else {
          bValue = new Date(bValue || 0);
        }
      }
      
      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    // Then apply pagination
    this.paginatedUsers = sorted.slice(this.startIndex, this.endIndex);
  }

  // Sorting methods
  sortTable(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    
    this.currentPage = 1; // Reset to first page when sorting
    this.updatePagination();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchTerm = filterValue;
    this.filterUsers();
  }

  onRoleFilterChange(): void {
    this.filterUsers();
  }

  private filterUsers(): void {
    let filtered = this.users;

    // Filter by search term
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.mobile.includes(this.searchTerm)
      );
    }

    // Filter by role
    if (this.selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === this.selectedRole);
    }

    this.filteredUsers = filtered;
    this.currentPage = 1; // Reset to first page when filtering
    this.updatePagination();
  }

  private updateStatistics(): void {
    this.statistics = {
      totalUsers: this.users.length,
      totalAdmins: this.users.filter(u => u.role === 'admin').length,
      totalSuperAdmins: this.users.filter(u => u.role === 'super_admin').length,
      activeUsers: this.users.filter(u => (u.status || 'active') === 'active').length
    };
  }

  // Permission checks
  canManageUser(user: User): boolean {
    if (!this.currentUser) return false;
    if (this.currentUser.role === 'super_admin') return true;
    if (this.currentUser.role === 'admin' && user.role === 'user') return true;
    return false;
  }

  canManageAdmins(): boolean {
    return this.currentUser?.role === 'super_admin' || false;
  }

  canManageSuperAdmins(): boolean {
    return this.currentUser?.role === 'super_admin' || false;
  }

  canChangeUserRole(user: User): boolean {
    if (!this.currentUser) return false;
    if (this.currentUser.role !== 'super_admin') return false;
    if (user.role === 'super_admin') return false;
    if (user.role === 'admin') return true;
    return false;
  }

  // Modal operations
  openCreateModal(): void {
    this.showCreateModal = true;
    this.createUserForm.reset({
      gender: 'male',
      role: 'user'
    });
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.createUserForm.reset();
  }

  openEditModal(user: User): void {
    this.selectedUser = user;
    this.editUserForm.patchValue({
      name: user.name,
      email: user.email,
      gender: user.gender,
      role: user.role,
      dateOfBirth: user.dateOfBirth,
      fullAddress: user.fullAddress,
      altMobile: user.altMobile
    });
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedUser = null;
    this.editUserForm.reset();
  }

  openDeleteModal(user: User): void {
    this.selectedUser = user;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedUser = null;
  }

  openRoleModal(user: User): void {
    this.selectedUser = user;
    this.showRoleModal = true;
  }

  closeRoleModal(): void {
    this.showRoleModal = false;
    this.selectedUser = null;
  }

  openStatusModal(user: User): void {
    this.selectedUser = user;
    this.showStatusModal = true;
  }

  closeStatusModal(): void {
    this.showStatusModal = false;
    this.selectedUser = null;
  }

  // User operations
  createUser(): void {
    if (this.createUserForm.invalid) return;

    const userData = this.createUserForm.value;
    const sub = this.userManagementService.createUser(userData).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.success('User created successfully!');
          this.closeCreateModal();
          this.loadUsers();
        } else {
          this.toastService.error(response.message);
        }
      },
      error: (error: any) => {
        console.error('Error creating user:', error);
        this.toastService.error('Failed to create user');
      }
    });
    this.subscriptions.push(sub);
  }

  updateUser(): void {
    if (this.editUserForm.invalid || !this.selectedUser) return;

    const userData = this.editUserForm.value;
    const sub = this.userManagementService.updateUser(this.selectedUser.id, userData).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.success('User updated successfully!');
          this.closeEditModal();
          this.loadUsers();
        } else {
          this.toastService.error(response.message);
        }
      },
      error: (error: any) => {
        console.error('Error updating user:', error);
        this.toastService.error('Failed to update user');
      }
    });
    this.subscriptions.push(sub);
  }

  deleteUser(): void {
    if (!this.selectedUser) return;

    const sub = this.userManagementService.deleteUser(this.selectedUser.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.success('User deleted successfully!');
          this.closeDeleteModal();
          this.loadUsers();
        } else {
          this.toastService.error(response.message);
        }
      },
      error: (error: any) => {
        console.error('Error deleting user:', error);
        this.toastService.error('Failed to delete user');
      }
    });
    this.subscriptions.push(sub);
  }

  changeUserRole(newRole: string): void {
    if (!this.selectedUser) return;

    const sub = this.userManagementService.changeUserRole(this.selectedUser.id, newRole as 'user' | 'admin' | 'super_admin').subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.success(`User role changed to ${newRole}!`);
          this.closeRoleModal();
          this.loadUsers();
        } else {
          this.toastService.error(response.message);
        }
      },
      error: (error: any) => {
        console.error('Error changing user role:', error);
        this.toastService.error('Failed to change user role');
      }
    });
    this.subscriptions.push(sub);
  }

  toggleUserStatus(activate: boolean): void {
    if (!this.selectedUser) return;

    const sub = this.userManagementService.toggleUserStatus(this.selectedUser.id, activate).subscribe({
      next: (response) => {
        if (response.success) {
          const status = activate ? 'active' : 'inactive';
          this.toastService.success(`User status changed to ${status}!`);
          this.closeStatusModal();
          this.loadUsers();
        } else {
          this.toastService.error(response.message);
        }
      },
      error: (error: any) => {
        console.error('Error changing user status:', error);
        this.toastService.error('Failed to change user status');
      }
    });
    this.subscriptions.push(sub);
  }

  // Utility methods
  copyToClipboard(text: string, type: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastService.success(`${type} copied to clipboard!`);
    }).catch(() => {
      this.toastService.error('Failed to copy to clipboard');
    });
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    
    try {
      let jsDate: Date;
      
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && date.toDate) {
        jsDate = date.toDate();
      }
      // Handle regular Date object
      else if (date instanceof Date) {
        jsDate = date;
      }
      // Handle timestamp number (milliseconds since epoch)
      else if (typeof date === 'number') {
        jsDate = new Date(date);
      }
      // Handle string date
      else if (typeof date === 'string') {
        jsDate = new Date(date);
      }
      // Handle other cases
      else {
        jsDate = new Date(date);
      }
      
      // Check if the date is valid
      if (isNaN(jsDate.getTime())) {
        return 'N/A';
      }
      
      // Format the date in a user-friendly way
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - jsDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      } else {
        return jsDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'N/A';
    }
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'super_admin': return 'badge-super-admin';
      case 'admin': return 'badge-admin';
      case 'user': return 'badge-user';
      default: return 'badge-user';
    }
  }

  getStatusBadgeClass(user: User): string {
    const status = user.status || 'active';
    return status === 'active' ? 'badge-active' : 'badge-inactive';
  }

  getSortIconClass(field: string): string {
    if (this.sortField !== field) {
      return 'fas fa-sort';
    }
    return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  }
}
